/**
 * Top-level orchestrator living inside the widget's shadow root.
 * Full-parity replacement for `CompanionManager.swift` on web — owns
 * every feature the native app exposes:
 *
 *   - Sprite + movement + nest-when-idle
 *   - Typed input, push-to-talk voice input, Hey Koko wake word
 *   - Claude streaming with memory injection and model selection
 *   - ResponseBubble for voice-mode; TextResponsePanel for text-only
 *   - ElevenLabs TTS (skipped in text-only mode)
 *   - 7-event sound effects engine + startup audio bed
 *   - Live mic waveform during listening
 *   - Proactive observations on a configurable interval
 *   - Ctrl+Shift+T text-mode toggle with 1600 ms feedback pill
 *   - Session toggle that pauses everything at once
 *   - "Show Koko" transient cursor mode
 *   - Pointing flight on [POINT:...] tags
 *
 * All persistent settings live in `SettingsStore`; subcontrollers
 * subscribe to the store so panel changes propagate without this
 * class mediating every signal.
 */

import type {
  KokoConfig,
  KokoExtensions,
  PageContext,
  PointingTag,
  ToolUseBlock,
} from "../types";
import type { KokoAnimationStateName } from "../sprite/animations";
import { ConversationHistory } from "../claude/conversationHistory";
import { ClaudeClient } from "../claude/ClaudeClient";
import { buildSystemPrompt, WEB_OBSERVATION_PROMPT } from "../claude/systemPrompt";
import { buildPageContext, releasePageContext } from "../perception/PageContextBuilder";
import {
  parseFinalResponse,
  stripPointingTagFromStreamingText,
} from "../pointing/PointingTagParser";
import { FlightController } from "../pointing/FlightController";
import { MovementController } from "../sprite/MovementController";
import {
  SpriteRenderer,
  type BirdRenderer,
  type PresentableBirdRenderer,
} from "../sprite/SpriteRenderer";
import { VideoRenderer } from "../sprite/VideoRenderer";
import type { BirdStyleOption } from "../sprite/birdVariants";
import { TypedInputBubble } from "../input/TypedInputBubble";
import { ResponseBubble } from "../output/ResponseBubble";
import { TextResponsePanel } from "../output/TextResponsePanel";
import { PointingCalloutBubble } from "../output/PointingCalloutBubble";
import type { VoiceInputController } from "../input/VoiceInputController";
import type { TTSPlayer } from "../output/TTSPlayer";
import { WakeWordListener } from "../input/WakeWordListener";
import { ControlPanel } from "../ui/ControlPanel";
import { StatusButton } from "../ui/StatusButton";
import { FeedbackPill } from "../ui/FeedbackPill";
import { StartupAudio } from "../audio/StartupAudio";
import { SoundEffects } from "../audio/SoundEffects";
import { Waveform } from "../sprite/Waveform";
import { NestController } from "../sprite/NestController";
import { ProactiveObserver } from "../observe/ProactiveObserver";
import { KokoMemoryStore } from "../memory/KokoMemoryStore";
import { MemoryEditor } from "../memory/MemoryEditor";
import { summarizeTurnInBackground } from "../memory/summarizeTurn";
import { VoiceStateStore } from "./state";
import { buildDefaultSettings, SettingsStore } from "./SettingsStore";
import {
  createKokoActionAgent,
  type KokoActionAgent,
} from "../agent/KokoActionAgent";

const SLASH_HOTKEY = "/";

/**
 * Push-to-talk hotkeys. Both fire the same handler — we accept either
 * because macOS intercepts Ctrl+Space by default ("Select previous
 * input source") so the browser may never see it. Ctrl+Backtick has no
 * default OS binding on macOS / Windows / Linux and reliably reaches
 * the page.
 */
type PushToTalkBinding = { key: string; modifier: "ctrlKey" | "metaKey" };
const PUSH_TO_TALK_BINDINGS: PushToTalkBinding[] = [
  { key: " ", modifier: "ctrlKey" },
  { key: "`", modifier: "ctrlKey" },
];
const TRANSIENT_LINGER_MS = 1000;
const TRANSIENT_FADE_MS = 400;
/**
 * The synthetic user prompt that drives the post-action "report back"
 * turn. Defined once so the outgoing request and the history append
 * use the identical string — they MUST match so the persisted history
 * mirrors what the API actually saw.
 */
const REPORT_BACK_USER_TEXT = "(report back on what you just did)";

interface KokoAppAssetUrls {
  spriteBaseUrl: string;
  audioBaseUrl: string;
  birdBaseUrl?: string;
  birdStyles: readonly BirdStyleOption[];
  defaultBirdStyleId: string;
}

interface DisposableBirdRenderer extends BirdRenderer {
  destroy(): void;
  whenReady?(): Promise<void>;
}

export class KokoApp {
  private readonly shadowRoot: ShadowRoot;
  private readonly config: KokoConfig;
  private readonly assetUrls: KokoAppAssetUrls;
  private readonly rootContainer: HTMLDivElement;
  private readonly birdContainer: HTMLDivElement;
  private readonly birdVideoLayer: HTMLDivElement;
  private readonly birdImage: HTMLImageElement;
  private readonly statusButton: StatusButton;
  private readonly settings: SettingsStore;
  private readonly spriteRenderer: BirdRenderer;
  private activeBirdRenderer: DisposableBirdRenderer;
  private readonly movementController: MovementController;
  private readonly typedInputBubble: TypedInputBubble;
  private readonly responseBubble: ResponseBubble;
  private readonly textResponsePanel: TextResponsePanel;
  private readonly pointingCalloutBubble: PointingCalloutBubble;
  private readonly controlPanel: ControlPanel;
  private readonly feedbackPill: FeedbackPill;
  private readonly memoryStore: KokoMemoryStore;
  private readonly memoryEditor: MemoryEditor;
  private readonly flightController: FlightController;
  private readonly claudeClient: ClaudeClient;
  private readonly conversationHistory: ConversationHistory;
  private readonly voiceState: VoiceStateStore;
  private readonly startupAudio: StartupAudio;
  private readonly soundEffects: SoundEffects;
  private readonly waveform: Waveform;
  private readonly wakeWordListener: WakeWordListener;
  private readonly nestController: NestController;
  private readonly proactiveObserver: ProactiveObserver;
  private readonly birdStyleOptions: readonly BirdStyleOption[];
  private currentBirdStyleId: string;
  private currentBirdAnimationState: KokoAnimationStateName = "flight";
  private currentBirdAnimationAltId: string | null = null;
  private rendererRunning = false;
  private pendingBirdRenderer: DisposableBirdRenderer | null = null;
  /**
   * Lets host pages drive choreographed UI actions (fly + type +
   * click) instead of mutating state silently. Exposed via
   * `getActionAgent()` so extensions like the studio bridge can
   * use it. The agent is a thin wrapper around the existing
   * MovementController + SpriteRenderer.
   */
  private readonly actionAgent: KokoActionAgent;

  private voiceInputController: VoiceInputController | null = null;
  private ttsPlayer: TTSPlayer | null = null;

  /**
   * Optional host-page extensions: custom tools, extra system-prompt
   * context, directive-tag handlers. Null when KokoApp is running
   * standalone (the marketing widget). Set via `setExtensions` after
   * construction so the studio page can register itself once mounted.
   */
  private extensions: KokoExtensions | null = null;

  private pageContextAtTurn: PageContext | null = null;
  private isPushToTalkHeld = false;
  private lastCursorPosition = { x: 0, y: 0 };
  private transientHideTimerId: number | null = null;
  /**
   * The nest corner the bird was perched at when the current
   * interaction began, or null if it wasn't nested. Set by every
   * user-action wake (slash hotkey, push-to-talk, wake word) BEFORE
   * `pokeActivity` clears the nest state. Consumed by `runPointing`
   * so the bird re-perches at the same corner after pointing —
   * mirrors native's `savedNestPosition` pattern.
   */
  private nestTargetBeforeWake: { viewportX: number; viewportY: number } | null = null;

  constructor(shadowRoot: ShadowRoot, config: KokoConfig, assetUrls: KokoAppAssetUrls) {
    this.shadowRoot = shadowRoot;
    this.config = config;
    this.assetUrls = assetUrls;
    this.voiceState = new VoiceStateStore();
    this.conversationHistory = new ConversationHistory();
    this.claudeClient = new ClaudeClient(config.api);
    this.birdStyleOptions = assetUrls.birdStyles;
    this.currentBirdStyleId = assetUrls.defaultBirdStyleId;
    this.settings = new SettingsStore(buildDefaultSettings(config, this.currentBirdStyleId));
    this.memoryStore = new KokoMemoryStore(this.settings);
    this.startupAudio = new StartupAudio({ audioBaseUrl: assetUrls.audioBaseUrl });
    this.soundEffects = new SoundEffects({
      audioBaseUrl: assetUrls.audioBaseUrl,
      settings: this.settings,
    });

    this.rootContainer = document.createElement("div");
    this.rootContainer.className = "koko-root";
    this.shadowRoot.appendChild(this.rootContainer);

    this.birdContainer = document.createElement("div");
    this.birdContainer.className = "koko-bird";
    this.birdImage = document.createElement("img");
    this.birdImage.alt = "";
    this.birdImage.draggable = false;
    this.birdContainer.appendChild(this.birdImage);
    this.birdVideoLayer = document.createElement("div");
    this.birdVideoLayer.className = "koko-bird-video-layer";
    this.birdContainer.appendChild(this.birdVideoLayer);
    this.rootContainer.appendChild(this.birdContainer);

    this.statusButton = new StatusButton({
      onClick: () => this.controlPanel.toggle(),
    });
    this.statusButton.mount(this.rootContainer);

    this.activeBirdRenderer = this.createRendererForStyle(
      this.resolveEffectiveBirdStyleId(),
    );
    this.spriteRenderer = this.createBirdRendererProxy();
    this.movementController = new MovementController(
      this.birdContainer,
      this.spriteRenderer,
      (isIdle) => {
        if (this.voiceState.get() === "idle") {
          this.spriteRenderer.setAnimation(isIdle ? "perched" : "flight");
        }
      },
      // The controller may drive flight ↔ swoop ↔ perched only while the
      // bird is in passive cursor-follow. Otherwise voice state owns the
      // sprite (listening / thinking / talking) and we must not stomp it.
      () => this.voiceState.get() === "idle",
    );

    this.typedInputBubble = new TypedInputBubble({
      placeholder: config.greeting,
      getBirdCenter: () => this.movementController.getSpriteCenter(),
      onSubmit: (value) => this.handleTypedSubmit(value),
      onDismiss: () => this.typedInputBubble.hide(),
    });
    this.typedInputBubble.mount(this.rootContainer);

    this.responseBubble = new ResponseBubble({
      getBirdCenter: () => this.movementController.getSpriteCenter(),
    });
    this.responseBubble.mount(this.rootContainer);

    this.textResponsePanel = new TextResponsePanel({
      getBirdCenter: () => this.movementController.getSpriteCenter(),
      onDismiss: () => this.textResponsePanel.hide(),
    });
    this.textResponsePanel.mount(this.rootContainer);

    this.pointingCalloutBubble = new PointingCalloutBubble({
      getBirdCenter: () => this.movementController.getSpriteCenter(),
    });
    this.pointingCalloutBubble.mount(this.rootContainer);

    this.waveform = new Waveform({
      getBirdCenter: () => this.movementController.getSpriteCenter(),
    });
    this.waveform.mount(this.rootContainer);

    this.memoryEditor = new MemoryEditor({ store: this.memoryStore });
    this.memoryEditor.mount(this.rootContainer);

    this.feedbackPill = new FeedbackPill({
      getBirdCenter: () => this.movementController.getSpriteCenter(),
    });
    this.feedbackPill.mount(this.rootContainer);

    this.controlPanel = new ControlPanel({
      getAnchorPoint: () => this.statusButton.getAnchorPoint(),
      settings: this.settings,
      wakeWordSupported: WakeWordListener.isSupported(),
      birdStyles: this.birdStyleOptions,
      onEditMemory: () => {
        // Close the panel first so it can't intercept clicks inside the
        // editor modal sitting on top of it.
        this.controlPanel.hide();
        this.memoryEditor.show();
      },
    });
    this.controlPanel.mount(this.rootContainer);

    this.wakeWordListener = new WakeWordListener({
      isOutputtingAudio: () => this.ttsPlayer?.isPlaying() ?? false,
      onDetected: () => this.handleWakeWordDetected(),
      onError: (error) => {
        // Surface to console with a clear prefix; flash the pill on fatal
        // errors so the user knows the toggle silently failed (browser
        // denied mic permission, unsupported, etc).
        console.warn(`[koko] wake word ${error.code}: ${error.message}`);
        if (error.fatal) {
          this.feedbackPill.flash("HEY KOKO BLOCKED");
          // Snap the panel toggle back off so the user sees it's not on.
          this.settings.set("wakeWordEnabled", false);
        }
      },
      onStatusChange: (status) => {
        this.controlPanel.setWakeWordStatus(status);
      },
      onTranscriptHeard: () => {
        // Re-pull the full ring buffer so order matches the listener's
        // own view; the panel then renders newest-first.
        this.controlPanel.setWakeWordTranscripts(
          this.wakeWordListener.getRecentTranscripts(),
        );
      },
    });

    this.nestController = new NestController({
      settings: this.settings,
      movementController: this.movementController,
      getLastCursorPosition: () => this.lastCursorPosition,
      // Lazily pull host-supplied perch targets (e.g. the studio's
      // branch tips). Mirrors the `getLastCursorPosition` closure so the
      // value reflects whatever extensions are registered at nest time,
      // even though they're set after construction via `setExtensions`.
      getPerchPoints: () => this.extensions?.getPerchPoints?.(),
    });

    this.proactiveObserver = new ProactiveObserver({
      settings: this.settings,
      onFire: () => this.runProactiveObservation(),
    });

    this.flightController = new FlightController(this.movementController);
    this.actionAgent = createKokoActionAgent({
      movementController: this.movementController,
      spriteRenderer: this.spriteRenderer,
    });
    this.wireVoiceStateToSprite();
    this.wireSettingsReactions();
  }

  private createBirdRendererProxy(): BirdRenderer {
    return {
      setAnimation: (stateName, altId) => {
        this.currentBirdAnimationState = stateName;
        this.currentBirdAnimationAltId = altId ?? null;
        this.activeBirdRenderer.setAnimation(stateName, altId ?? undefined);
        this.pendingBirdRenderer?.setAnimation(stateName, altId ?? undefined);
      },
      start: () => {
        this.rendererRunning = true;
        this.activeBirdRenderer.start();
        this.pendingBirdRenderer?.start();
      },
      stop: () => {
        this.rendererRunning = false;
        this.activeBirdRenderer.stop();
        this.pendingBirdRenderer?.stop();
      },
    };
  }

  private createRendererForStyle(
    styleId: string,
  ): DisposableBirdRenderer {
    const style = this.resolveBirdStyleOption(styleId);
    this.currentBirdStyleId = style.id;
    if (style.kind === "video" && style.variant) {
      const fallbackRenderer = new SpriteRenderer(
        this.birdImage,
        this.assetUrls.spriteBaseUrl,
      );
      const renderer = new VideoRenderer(
        this.birdVideoLayer,
        this.birdImage,
        fallbackRenderer,
        this.assetUrls.birdBaseUrl ?? "",
        style.variant,
        style.scale,
      ) as DisposableBirdRenderer;
      return renderer;
    }

    const renderer = new SpriteRenderer(
      this.birdImage,
      this.assetUrls.spriteBaseUrl,
    ) as DisposableBirdRenderer;
    return renderer;
  }

  private resolveBirdStyleOption(styleId: string): BirdStyleOption {
    return (
      this.birdStyleOptions.find((option) => option.id === styleId) ??
      this.birdStyleOptions[0]
    );
  }

  private resolveEffectiveBirdStyleId(): string {
    const storedStyle = this.settings.get("birdStyle");
    return this.birdStyleOptions.some((option) => option.id === storedStyle)
      ? storedStyle
      : this.currentBirdStyleId;
  }

  private swapBirdStyle(styleId: string): void {
    const nextStyle = this.resolveBirdStyleOption(styleId);
    if (nextStyle.id === this.currentBirdStyleId) return;

    const nextRenderer = this.createRendererForStyle(nextStyle.id);
    nextRenderer.setAnimation(
      this.currentBirdAnimationState,
      this.currentBirdAnimationAltId ?? undefined,
    );
    if (this.rendererRunning) {
      nextRenderer.start();
    } else {
      nextRenderer.stop();
    }

    const previousRenderer = this.activeBirdRenderer;
    this.pendingBirdRenderer = nextRenderer;
    void this.whenBirdRendererReady(nextRenderer).then(() => {
      if (this.pendingBirdRenderer !== nextRenderer) {
        nextRenderer.destroy();
        return;
      }
      this.pendingBirdRenderer = null;
      if (nextRenderer instanceof SpriteRenderer) {
        this.birdImage.style.visibility = "visible";
        this.birdImage.style.opacity = "1";
        this.birdVideoLayer.style.opacity = "0";
      }
      this.activeBirdRenderer = nextRenderer;
      previousRenderer.destroy();
    });
  }

  /**
   * Public accessor for the action agent. Host pages (like the
   * studio bridge) call this once after the app starts to get the
   * choreography primitives.
   */
  getActionAgent(): KokoActionAgent {
    return this.actionAgent;
  }

  /**
   * Flash a small status pill near the bird. Used by host pages
   * (the studio bridge) to confirm completed actions like
   * "+ mei" or "✓ storyboard ready" without bloating the response
   * bubble. Variant defaults to the standard cream pill; pass
   * "error" for a red one.
   */
  flashPill(text: string, variant: "standard" | "error" = "standard"): void {
    this.feedbackPill.flash(
      text,
      variant === "error" ? { variant: "error" } : { variant: "info" },
    );
  }

  /**
   * Fire a follow-up "report back" Claude call after a host-page
   * choreography has finished. The studio bridge calls this with
   * a list of completed actions ("+ mei", "~ storyboard ready",
   * etc.) — Koko produces a short conversational reply about what
   * just got done. Streams into the response bubble (or text panel
   * in text-only mode) and plays via TTS like a normal turn.
   *
   * No tools, no screenshot, no [POINT:] / [OPEN:] tags — this is
   * pure voice/dialogue. Conversation history gets the assistant
   * turn so future turns know what was done.
   *
   * Aborts silently if the user has already interrupted (the
   * voiceState is back at processing/listening from a new turn).
   */
  async reportBack(actionsCompleted: readonly string[]): Promise<void> {
    if (actionsCompleted.length === 0) return;
    if (!this.settings.get("sessionActive")) return;
    // If the user already moved on to a new turn, drop the report.
    const currentVoiceState = this.voiceState.get();
    if (currentVoiceState !== "idle") return;

    const summarizedActions = actionsCompleted
      .map((entry, idx) => `${idx + 1}. ${entry}`)
      .join("\n");
    const reportBackUserText = REPORT_BACK_USER_TEXT;
    const reportBackSystemPrompt = [
      "you're koko. you just finished performing some actions in the user's video-production studio. give a SHORT conversational follow-up — one or two sentences, casual, lowercase, koko's normal voice.",
      "",
      "rules:",
      "- acknowledge what got done (paraphrase, don't recite a list).",
      "- if there's a natural next step, suggest it as one short clause at the end. otherwise just confirm.",
      "- write for the ear (this gets spoken aloud).",
      "- no markdown, no tool calls, no [POINT:...] or [OPEN:...] tags.",
      "",
      "completed actions:",
      summarizedActions,
    ].join("\n");

    const isTextOnlyMode = this.settings.get("textOnlyMode");
    const surface = isTextOnlyMode ? this.textResponsePanel : this.responseBubble;

    this.voiceState.set("processing");
    this.soundEffects.play("think");
    surface.beginStreaming();

    let firstChunkSeen = false;
    let accumulatedText = "";
    let finalText = "";

    // Reuse the most recent page context if we still have it,
    // otherwise build a tiny placeholder. The report-back doesn't
    // need to inspect the page — Claude already saw it during the
    // turn that produced the actions we're summarizing.
    const fallbackContext: PageContext = this.pageContextAtTurn ?? {
      domSummary: "",
      screenshotBase64:
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=",
      screenshotMediaType: "image/png",
      viewportWidth: 1,
      viewportHeight: 1,
    };

    await this.claudeClient.streamResponse(
      {
        systemPrompt: reportBackSystemPrompt,
        history: this.conversationHistory.snapshot(),
        userText: reportBackUserText,
        pageContext: fallbackContext,
        model: this.resolveClaudeModelId(),
        webSearchEnabled: false,
        // No custom tools — this is a pure dialogue turn.
        customTools: [],
      },
      {
        onTextChunk: (chunk) => {
          if (!firstChunkSeen) {
            firstChunkSeen = true;
            this.voiceState.set("responding");
          }
          accumulatedText += chunk;
          surface.updateStreaming(accumulatedText);
        },
        onComplete: (fullText) => {
          finalText = fullText.trim();
          surface.updateStreaming(finalText);
          surface.finishStreaming();
          if (finalText.length > 0) {
            // Persist the synthetic report-back exchange as a real
            // user→assistant PAIR. Without the user turn, the preceding
            // `assistant(tool_use)` message would sit directly before
            // this assistant message in history; the next real turn
            // would then replay a `tool_use` block with no following
            // `tool_result` and Anthropic 400s ("tool_use ids ... were
            // found without tool_result blocks immediately after").
            this.conversationHistory.appendUserMessage(reportBackUserText);
            this.conversationHistory.appendAssistantMessage(finalText);
          }
        },
        onError: (error) => {
          console.warn("[koko] report-back failed:", error);
          surface.finishStreaming();
        },
      },
    );

    if (finalText.length === 0) {
      this.voiceState.set("idle");
      return;
    }

    this.soundEffects.play("done");

    if (!isTextOnlyMode && this.settings.get("voiceEnabled")) {
      try {
        await this.speakIfEnabled(finalText);
      } catch (error) {
        console.warn("[koko] report-back TTS failed:", error);
      }
    }

    this.voiceState.set("idle");
    this.scheduleTransientHideIfEnabled();
  }

  /**
   * Register or replace the host-page extension surface. Pass `null`
   * to clear any previously registered extensions and revert to
   * stock widget behavior. Safe to call before or after `start()`.
   */
  setExtensions(extensions: KokoExtensions | null): void {
    this.extensions = extensions;
  }

  async start(): Promise<void> {
    this.spriteRenderer.start();
    this.movementController.startTrackingCursor();
    this.installCursorPositionTracker();
    this.registerGlobalHotkeys();
    void this.startupAudio.playIntro();

    if (this.settings.get("wakeWordEnabled")) {
      void this.wakeWordListener.enable();
    }

    this.nestController.start();
    this.proactiveObserver.start();
    this.applyCursorVisibilityMode();

    await this.movementController.playIntroFlight();
  }

  destroy(): void {
    this.claudeClient.cancelInFlight();
    this.flightController.destroy();
    this.movementController.destroy();
    this.spriteRenderer.stop();
    this.activeBirdRenderer.destroy();
    this.pendingBirdRenderer?.destroy();
    this.pendingBirdRenderer = null;
    this.typedInputBubble.destroy();
    this.responseBubble.destroy();
    this.textResponsePanel.destroy();
    this.pointingCalloutBubble.destroy();
    this.controlPanel.destroy();
    this.feedbackPill.destroy();
    this.waveform.destroy();
    this.memoryEditor.destroy();
    this.statusButton.destroy();
    this.wakeWordListener.disable();
    this.nestController.stop();
    this.proactiveObserver.stop();
    this.startupAudio.destroy();
    this.removeGlobalHotkeys();
    if (this.transientHideTimerId !== null) {
      window.clearTimeout(this.transientHideTimerId);
    }
    releasePageContext();
    this.rootContainer.remove();
  }

  private handleTypedSubmit(userText: string): void {
    this.typedInputBubble.hide();
    void this.runConversationTurn(userText);
  }

  private async runConversationTurn(userText: string): Promise<void> {
    if (!this.settings.get("sessionActive")) return;
    if (userText.trim().length === 0) return;

    // Pull the nest target snapshotted by whichever user action started
    // this turn (slash press, PTT keydown, wake-word detection). Falls
    // back to `getCurrentNestTarget` for any flow that didn't capture
    // ahead of time. Either way `runPointing` reads it later via the
    // explicit parameter — by that point the bird may have been awake
    // from the nest for several seconds, so checking nest state at
    // pointing time would be too late.
    const nestTargetAtTurnStart =
      this.nestTargetBeforeWake ?? this.nestController.getCurrentNestTarget();
    this.nestTargetBeforeWake = null;

    this.pokeActivity();

    const isTextOnlyMode = this.settings.get("textOnlyMode");
    this.revealBirdIfTransient();

    this.voiceState.set("processing");
    this.soundEffects.play("think");
    const activeResponseSurface = isTextOnlyMode
      ? this.textResponsePanel
      : this.responseBubble;
    activeResponseSurface.beginStreaming();
    this.conversationHistory.appendUserMessage(userText);

    let pageContext: PageContext;
    try {
      const result = await buildPageContext();
      pageContext = result.context;
      this.pageContextAtTurn = pageContext;
    } catch (error) {
      this.failTurn(`couldn't read the page — ${(error as Error).message}`, activeResponseSurface);
      return;
    }

    let accumulatedRaw = "";
    let finalDisplayText = "";
    let finalPointingTag: PointingTag | null = null;
    let firstChunkSeen = false;
    /**
     * Set true when an extension's directive parser fires a panel
     * switch. KokoApp delays the pointing flight until the new panel
     * has rendered so the bird never flies to a stale screenshot
     * coordinate from the old layout (the "ghost point" bug).
     */
    let shouldDeferPointingForRepaint = false;
    /**
     * Set true when an extension explicitly takes over the bird
     * (e.g. the studio's choreographed add-character sequence). In
     * that case any `[POINT:...]` flight from this turn is dropped
     * — overlaying it on top would yank the bird away from its
     * choreographed task.
     */
    let shouldCancelPointing = false;
    const completedToolUses: ToolUseBlock[] = [];
    const activeExtensions = this.extensions;
    /**
     * Whether to chunk Claude's response by sentence and feed each
     * sentence to the TTS queue as it arrives — text reveals in the
     * bubble in lockstep with the spoken word. When false (text-only
     * mode or voice toggled off), fall back to the old "stream text
     * immediately" path.
     */
    const useChunkedTTS = !isTextOnlyMode && this.settings.get("voiceEnabled");
    /** How many characters of stripped display text we've already
     *  enqueued to TTS. Sentences past this point haven't been sent. */
    let chunkedTtsCommittedChars = 0;
    /** Promises for each enqueued segment so the post-response
     *  pipeline can wait on the audio actually finishing. */
    const ttsSegmentPromises: Promise<void>[] = [];
    if (useChunkedTTS) {
      // Pre-load TTSPlayer; the first sentence gets enqueued before
      // we'd otherwise lazy-import it.
      await this.ensureTtsPlayer();
    }

    const enqueueSegment = (segmentText: string): void => {
      if (!useChunkedTTS || segmentText.length === 0) return;
      const player = this.ttsPlayer;
      if (!player) return;
      ttsSegmentPromises.push(
        player.speak(segmentText, {
          onAudioStart: () =>
            this.responseBubble.revealSpokenSegment(segmentText),
        }),
      );
    };

    // Voice state stays in "processing" (→ thinking animation) for the
    // whole pre-first-chunk wait. Flipping to "responding" prematurely
    // would skip the thinking pose entirely on slow turns.
    await this.claudeClient.streamResponse(
      {
        systemPrompt: this.buildFullSystemPrompt(activeExtensions),
        history: this.conversationHistory.snapshot().slice(0, -1),
        userText,
        pageContext,
        model: this.resolveClaudeModelId(),
        webSearchEnabled: this.settings.get("webSearchEnabled"),
        customTools: activeExtensions?.tools,
      },
      {
        onTextChunk: (chunk) => {
          if (!firstChunkSeen) {
            firstChunkSeen = true;
            this.voiceState.set("responding");
          }
          accumulatedRaw += chunk;
          const display = this.stripAllStreamingTags(
            accumulatedRaw,
            activeExtensions,
          );
          if (useChunkedTTS) {
            // Slice off newly-completed sentence(s) past the last
            // committed boundary and enqueue them. The bubble will
            // reveal each as its audio plays.
            const newSegment = sliceCompletedSentences(
              display,
              chunkedTtsCommittedChars,
            );
            if (newSegment.length > 0) {
              enqueueSegment(newSegment);
              chunkedTtsCommittedChars += newSegment.length;
            }
          } else {
            activeResponseSurface.updateStreaming(display);
          }
        },
        onToolUse: (block) => {
          completedToolUses.push(block);
          try {
            activeExtensions?.onToolUse?.(block);
          } catch (error) {
            console.warn(
              `[koko] extension onToolUse threw for ${block.name}:`,
              error,
            );
          }
        },
        onComplete: (fullText, toolUses) => {
          const parsed = parseFinalResponse(fullText);
          finalPointingTag = parsed.pointingTag;
          // After pointing tag is removed, run any extension-defined
          // directive parser (e.g. studio's `[OPEN:panel_id]`). It
          // fires its own side effects and returns the cleaned text
          // we'll show + persist.
          const directiveResult = activeExtensions?.parseAndStripFinalDirectives?.(
            parsed.displayText,
          );
          if (directiveResult === undefined) {
            finalDisplayText = parsed.displayText;
          } else if (typeof directiveResult === "string") {
            finalDisplayText = directiveResult;
          } else {
            finalDisplayText = directiveResult.cleanedText;
            shouldDeferPointingForRepaint =
              directiveResult.deferPointingUntilNextFrame ?? false;
            shouldCancelPointing = directiveResult.cancelPointing ?? false;
          }
          if (useChunkedTTS) {
            // Flush any tail past the last sentence boundary as the
            // final segment. finishStreaming is deferred to after the
            // queue drains so the auto-hide timer matches the audio.
            const tail = finalDisplayText.slice(chunkedTtsCommittedChars).trim();
            if (tail.length > 0) {
              enqueueSegment(tail);
              chunkedTtsCommittedChars = finalDisplayText.length;
            }
          } else {
            activeResponseSurface.updateStreaming(finalDisplayText);
            activeResponseSurface.finishStreaming();
          }
          this.persistAssistantTurn(
            finalDisplayText,
            toolUses.length > 0 ? toolUses : completedToolUses,
          );
        },
        onError: (error) => this.failTurn(error.message, activeResponseSurface),
      },
    );

    if (finalDisplayText.length === 0) {
      releasePageContext();
      this.voiceState.set("idle");
      this.scheduleTransientHideIfEnabled();
      return;
    }

    this.soundEffects.play("done");

    const postResponseTasks: Promise<unknown>[] = [];
    if (useChunkedTTS) {
      // Wait for the entire enqueued-during-streaming chain to drain
      // before we close out the bubble. allSettled ignores per-segment
      // errors — TTSPlayer already surfaces those via its onError.
      postResponseTasks.push(
        Promise.allSettled(ttsSegmentPromises).then(() => {
          // Belt-and-braces: ensure the final visible bubble text is
          // exactly `finalDisplayText`. If a segment got dropped due
          // to TTS failure, this fills it in.
          if (this.responseBubble) {
            this.responseBubble.updateStreaming(finalDisplayText);
            this.responseBubble.finishStreaming();
          }
        }),
      );
    } else if (!isTextOnlyMode && this.settings.get("voiceEnabled")) {
      // Voice was disabled at chunking time but is on now (settings
      // changed mid-turn) — fall back to one-shot speak.
      postResponseTasks.push(this.speakIfEnabled(finalDisplayText));
    }
    if (finalPointingTag && this.pageContextAtTurn && !shouldCancelPointing) {
      const pointingPageContext = this.pageContextAtTurn;
      const pointingTagAtTurn = finalPointingTag;
      postResponseTasks.push(
        (async () => {
          // If a panel switch fired this turn, wait for the browser to
          // lay out + paint the new panel before resolving the
          // pointing ref. Two RAFs is the standard "next paint settled"
          // dance — one for layout, one for the actual paint.
          if (shouldDeferPointingForRepaint) {
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => {
                requestAnimationFrame(() => resolve());
              }),
            );
          }
          await this.runPointing(
            pointingTagAtTurn,
            pointingPageContext,
            nestTargetAtTurnStart,
          );
        })(),
      );
    }
    await Promise.allSettled(postResponseTasks);

    releasePageContext();
    this.voiceState.set("idle");
    this.scheduleTransientHideIfEnabled();

    this.kickOffMemoryDistillation(userText, finalDisplayText, pageContext);
    this.proactiveObserver.resetTimer();
  }

  private async runPointing(
    tag: PointingTag,
    pageContextAtTurn: PageContext,
    explicitNestTarget?: { viewportX: number; viewportY: number } | null,
  ): Promise<void> {
    // Prefer the caller's snapshot when provided — `runConversationTurn`
    // captures the nest target *before* `pokeActivity` wakes the bird,
    // since by the time `runPointing` runs the nest state has already
    // been cleared. Proactive observations don't poke activity, so the
    // current nest state is still valid for them and we fall back here.
    const savedNestTarget =
      explicitNestTarget !== undefined
        ? explicitNestTarget
        : this.nestController.getCurrentNestTarget();

    const calloutLabel = (tag.label ?? "").trim();

    await this.flightController.pointAt(tag, pageContextAtTurn, {
      onArrival: () => {
        this.soundEffects.play("point");
        if (calloutLabel.length > 0) {
          this.pointingCalloutBubble.show(calloutLabel);
        }
      },
      onHoldComplete: () => {
        this.pointingCalloutBubble.hide();
        if (savedNestTarget) {
          // Re-perch at the same corner the bird flew out from.
          void this.nestController.returnToNestTarget(savedNestTarget);
        } else {
          this.movementController.returnToFollow();
        }
      },
    });
  }

  private async runProactiveObservation(): Promise<void> {
    if (!this.settings.get("sessionActive")) return;
    if (!this.settings.get("proactiveEnabled")) return;

    this.revealBirdIfTransient();
    this.voiceState.set("processing");
    this.soundEffects.play("think");
    this.textResponsePanel.beginStreaming();

    let pageContext: PageContext;
    try {
      const result = await buildPageContext();
      pageContext = result.context;
      this.pageContextAtTurn = pageContext;
    } catch (error) {
      this.failTurn(
        `couldn't read the page — ${(error as Error).message}`,
        this.textResponsePanel,
      );
      return;
    }

    let accumulatedRaw = "";
    let finalDisplayText = "";
    let finalPointingTag: PointingTag | null = null;
    let firstChunkSeen = false;

    await this.claudeClient.streamResponse(
      {
        // Observations don't inject memory; they're fresh perception.
        systemPrompt: WEB_OBSERVATION_PROMPT,
        history: [],
        userText: "(proactive observation — tell me what you see on this page)",
        pageContext,
        model: this.resolveClaudeModelId(),
        // Proactive observations are commentary on what's already on
        // screen — never the right time to spend budget on web search.
        webSearchEnabled: false,
      },
      {
        onTextChunk: (chunk) => {
          if (!firstChunkSeen) {
            firstChunkSeen = true;
            this.voiceState.set("responding");
          }
          accumulatedRaw += chunk;
          const display = stripPointingTagFromStreamingText(accumulatedRaw);
          this.textResponsePanel.updateStreaming(display);
        },
        onComplete: (fullText) => {
          const parsed = parseFinalResponse(fullText);
          finalDisplayText = parsed.displayText;
          finalPointingTag = parsed.pointingTag;
          this.textResponsePanel.updateStreaming(finalDisplayText);
          this.textResponsePanel.finishStreaming();
        },
        onError: (error) => this.failTurn(error.message, this.textResponsePanel),
      },
    );

    // `proactive observation` flow ignores tool_uses on purpose — it's
    // a commentary turn, not a state-changing one. Studio extensions
    // only register tools for explicit user-driven turns.

    if (finalDisplayText.length > 0) {
      this.soundEffects.play("done");
    }
    if (finalPointingTag && this.pageContextAtTurn) {
      await this.runPointing(finalPointingTag, this.pageContextAtTurn);
    }

    releasePageContext();
    this.voiceState.set("idle");
    this.scheduleTransientHideIfEnabled();
  }

  private buildMemoryContextBlock(): string | undefined {
    const memoryContent = this.memoryStore.loadForPrompt();
    if (!memoryContent) return undefined;
    return `your memory of past interactions with this user:\n${memoryContent}`;
  }

  /**
   * Compose the full per-turn system prompt:
   *   base prompt + memory context + extension addition.
   * Each section is separated by a blank line. The extension addition
   * is re-evaluated every turn so the LLM sees fresh workspace state.
   */
  private buildFullSystemPrompt(extensions: KokoExtensions | null): string {
    const sections: string[] = [];
    const memoryBlock = this.buildMemoryContextBlock();
    sections.push(buildSystemPrompt(memoryBlock));
    const extensionAddition = extensions?.buildSystemPromptAddition?.();
    if (extensionAddition && extensionAddition.trim().length > 0) {
      sections.push(extensionAddition);
    }
    return sections.join("\n\n");
  }

  /**
   * Strip every kind of inline directive (built-in pointing tag plus
   * any host-page directives) from a partial streaming text, so the
   * user-facing bubble never flashes raw metadata. Order matters:
   * pointing strip first, then extension strip — extension parsers
   * shouldn't have to know about the pointing format.
   */
  private stripAllStreamingTags(
    accumulatedRaw: string,
    extensions: KokoExtensions | null,
  ): string {
    const afterPointing = stripPointingTagFromStreamingText(accumulatedRaw);
    const stripExtensionTags = extensions?.stripDirectivesFromStreamingText;
    if (!stripExtensionTags) return afterPointing;
    return stripExtensionTags(afterPointing);
  }

  /**
   * Persist an assistant turn into history. Two paths:
   *   - tool_uses present → store as Anthropic content array so the
   *     next user turn can prepend matching tool_result blocks.
   *   - text only → keep the existing string-content fast path.
   */
  private persistAssistantTurn(
    finalDisplayText: string,
    toolUses: readonly ToolUseBlock[],
  ): void {
    if (toolUses.length === 0) {
      this.conversationHistory.appendAssistantMessage(finalDisplayText);
      return;
    }
    this.conversationHistory.appendAssistantMessageWithToolUses(
      finalDisplayText,
      toolUses,
    );
  }

  private kickOffMemoryDistillation(
    userText: string,
    assistantText: string,
    pageContext: PageContext,
  ): void {
    if (!this.settings.get("memoryEnabled")) return;
    // Fire-and-forget; we don't care about completion timing.
    void summarizeTurnInBackground(
      this.claudeClient,
      userText,
      assistantText,
      pageContext,
    )
      .then((summary) => {
        if (summary) this.memoryStore.appendSummary(summary);
      })
      .catch(() => {
        // Silently ignore — distillation failures shouldn't interrupt flow.
      });
  }

  /**
   * Lazy-load the TTSPlayer. Idempotent — repeated calls return the
   * same instance. Both the chunked-streaming pipeline and the
   * one-shot fallback go through this so they share configuration
   * (voice picker, error handling, playback-state wiring).
   */
  private async ensureTtsPlayer(): Promise<TTSPlayer> {
    if (this.ttsPlayer) return this.ttsPlayer;
    const module = await import("../output/TTSPlayer");
    this.ttsPlayer = new module.TTSPlayer({
      workerBaseUrl: this.config.api,
      // Resolve every utterance so a panel-driven voice change
      // takes effect immediately on the next response.
      getVoiceId: () => this.settings.get("voiceId"),
      onPlaybackStart: () => this.voiceState.set("responding"),
      onPlaybackEnd: () => this.voiceState.set("idle"),
      onError: (error) => {
        console.warn("[koko] TTS error:", error);
        this.soundEffects.play("error");
      },
    });
    return this.ttsPlayer;
  }

  private async speakIfEnabled(text: string): Promise<void> {
    const player = await this.ensureTtsPlayer();
    await player.speak(text);
  }

  private resolveClaudeModelId(): string {
    // Opus 4.7 is the latest at the time of this build; sonnet 4.6 is
    // the matching native sibling. The Worker just forwards whatever
    // we send, so swapping these is a one-line change.
    return this.settings.get("model") === "opus"
      ? "claude-opus-4-7"
      : "claude-sonnet-4-6";
  }

  private failTurn(
    reason: string,
    surface: ResponseBubble | TextResponsePanel,
  ): void {
    // Cancel any segments that were already enqueued before the
    // failure — otherwise the bird would keep speaking partial
    // sentences after the error message appeared.
    this.ttsPlayer?.stop();
    surface.updateStreaming(`something went wrong — ${reason}`);
    surface.finishStreaming();
    this.voiceState.set("idle");
    this.soundEffects.play("error");
    releasePageContext();
    this.scheduleTransientHideIfEnabled();
  }

  private wireVoiceStateToSprite(): void {
    this.voiceState.subscribe((next, previous) => {
      this.statusButton.setState(next);
      switch (next) {
        case "idle":
          // While the bird is perched at a nest corner, fall back to
          // the perched pose instead of "flight" so a proactive
          // observation finishing doesn't visually snap the bird out
          // of its idle nest pose. Mirrors `OverlayWindow.swift:632-634`
          // ("while nested, force perched on idle").
          this.spriteRenderer.setAnimation(
            this.nestController.isNested() ? "perched" : "flight",
          );
          this.waveform.hide();
          break;
        case "listening":
          this.spriteRenderer.setAnimation("listening");
          this.waveform.show();
          break;
        case "processing":
          this.spriteRenderer.setAnimation("thinking");
          this.waveform.hide();
          break;
        case "responding":
          this.spriteRenderer.setAnimation("talking");
          this.waveform.hide();
          break;
      }

      // Re-arm the wake-word listener when the turn fully settles. Native
      // Koko mirrors this in `bindWakeWordDetection` — the listener is
      // disabled while PTT/TTS owns the mic, then resumed once we're idle
      // again so it can't echo-trigger on Koko's own voice.
      if (next === "idle" && previous !== "idle") {
        if (
          this.settings.get("sessionActive") &&
          this.settings.get("wakeWordEnabled") &&
          !(this.ttsPlayer?.isPlaying() ?? false)
        ) {
          void this.wakeWordListener.enable();
        }
      }
    });
  }

  private wireSettingsReactions(): void {
    // Session toggle: gate the whole interaction graph.
    this.settings.subscribe("sessionActive", (enabled) => {
      if (!enabled) {
        this.claudeClient.cancelInFlight();
        this.conversationHistory.reset();
        this.wakeWordListener.disable();
        this.proactiveObserver.stop();
        this.nestController.stop();
        this.responseBubble.hide();
        this.textResponsePanel.hide();
        this.pointingCalloutBubble.hide();
        this.typedInputBubble.hide();
        this.birdContainer.style.opacity = "0";
        this.waveform.hide();
        this.voiceState.set("idle");
      } else {
        this.birdContainer.style.opacity = "";
        if (this.settings.get("wakeWordEnabled")) void this.wakeWordListener.enable();
        this.nestController.start();
        this.proactiveObserver.start();
        this.applyCursorVisibilityMode();
      }
    });

    // Wake word toggle.
    this.settings.subscribe("wakeWordEnabled", (enabled) => {
      if (!this.settings.get("sessionActive")) return;
      if (enabled) {
        void this.wakeWordListener.enable();
        this.feedbackPill.flash("HEY KOKO LISTENING");
      } else {
        this.wakeWordListener.disable();
      }
    });

    // Text-only mode — flash feedback pill, play toggle SFX.
    this.settings.subscribe("textOnlyMode", (enabled, previous) => {
      if (enabled === previous) return;
      this.feedbackPill.flash(enabled ? "TEXT MODE ON" : "TEXT MODE OFF");
      this.soundEffects.play("toggle");
    });

    // Cursor visibility mode — re-apply transient vs always on change.
    this.settings.subscribe("cursorVisibility", () => this.applyCursorVisibilityMode());

    this.settings.subscribe("birdStyle", (nextStyle) => {
      this.swapBirdStyle(nextStyle);
    });
  }

  private async whenBirdRendererReady(
    renderer: DisposableBirdRenderer,
  ): Promise<void> {
    const presentableRenderer = renderer as PresentableBirdRenderer;
    await presentableRenderer.whenReady?.();
  }

  private installCursorPositionTracker(): void {
    document.addEventListener(
      "mousemove",
      (event) => {
        // We only care about the latest cursor position — used by the
        // nest controller to pick the corner *furthest* from the cursor
        // when it eventually fires. We do NOT reset the idle timer here:
        // every `kokoNest.resetIdleTimer()` call in `CompanionManager.swift`
        // (lines 1135, 1279, 1493, 1566) is gated on a real command —
        // PTT press, typed submit, wake-word detection. Mouse movement
        // alone never resets the timer in native.
        this.lastCursorPosition = { x: event.clientX, y: event.clientY };
      },
      { passive: true },
    );
  }

  private applyCursorVisibilityMode(): void {
    const mode = this.settings.get("cursorVisibility");
    if (mode === "always") {
      this.birdContainer.style.transition = `opacity ${TRANSIENT_FADE_MS}ms ease`;
      this.birdContainer.style.opacity = "1";
      if (this.transientHideTimerId !== null) {
        window.clearTimeout(this.transientHideTimerId);
        this.transientHideTimerId = null;
      }
      return;
    }
    // Transient mode — fade the bird out at rest.
    this.birdContainer.style.transition = `opacity ${TRANSIENT_FADE_MS}ms ease`;
    if (this.voiceState.get() === "idle") {
      this.birdContainer.style.opacity = "0";
    }
  }

  private revealBirdIfTransient(): void {
    if (this.settings.get("cursorVisibility") !== "transient") return;
    this.birdContainer.style.opacity = "1";
    if (this.transientHideTimerId !== null) {
      window.clearTimeout(this.transientHideTimerId);
      this.transientHideTimerId = null;
    }
  }

  private scheduleTransientHideIfEnabled(): void {
    if (this.settings.get("cursorVisibility") !== "transient") return;
    if (this.transientHideTimerId !== null) {
      window.clearTimeout(this.transientHideTimerId);
    }
    this.transientHideTimerId = window.setTimeout(() => {
      this.transientHideTimerId = null;
      if (this.voiceState.get() === "idle") {
        this.birdContainer.style.opacity = "0";
      }
    }, TRANSIENT_LINGER_MS);
  }

  /**
   * Hard interrupt — cancel everything in flight so the user can
   * recover the bird's attention. Used by ESC and by any new-turn
   * hotkey (slash, push-to-talk) so a runaway response, a long
   * stretch of TTS, or a stuck choreography never blocks input.
   *
   *   - aborts the active Claude stream
   *   - stops the TTS player (if loaded and speaking)
   *   - cancels any in-flight host-page choreography (typing loop)
   *   - hides the response bubble + text response panel + pointing
   *     callout + waveform so the screen settles fast
   *   - drops the voice state to idle so the bird's animation
   *     returns to flight/perched without lingering on talking
   */
  private interruptEverything(): void {
    this.claudeClient.cancelInFlight();
    this.actionAgent.cancel();
    // Let any registered extension drop its pending follow-up work
    // (queued report-back calls, etc.) before we tear down state.
    try {
      this.extensions?.onUserInterrupt?.();
    } catch (error) {
      console.warn("[koko] extension onUserInterrupt threw:", error);
    }
    if (this.ttsPlayer) {
      // TTSPlayer has a synchronous stop() in some versions; for
      // safety we just hide the bubble and let any pending audio
      // finish its current chunk. Stopping mid-chunk would require
      // wiring an `interrupt()` into TTSPlayer.
      try {
        // Use a duck-typed call so we don't take a hard dependency
        // on a method that may not exist yet.
        const player = this.ttsPlayer as unknown as { stop?: () => void };
        player.stop?.();
      } catch (error) {
        console.warn("[koko] tts stop threw:", error);
      }
    }
    this.responseBubble.hide();
    this.textResponsePanel.hide();
    this.pointingCalloutBubble.hide();
    this.waveform.hide();
    this.voiceState.set("idle");
  }

  private pokeActivity(): void {
    // Snapshot the nest target *before* the reset wakes the bird —
    // captures into `nestTargetBeforeWake` so any pointing flight
    // later in this turn can re-perch at the same corner. Every user
    // wake site funnels through here (slash press, PTT begin, wake
    // word, text-mode hotkey) so this single capture covers them all.
    const currentNestTarget = this.nestController.getCurrentNestTarget();
    if (currentNestTarget) {
      this.nestTargetBeforeWake = currentNestTarget;
    }
    this.proactiveObserver.resetTimer();
    this.nestController.resetIdleTimer();
    // If a host-page choreography is mid-flight from a previous turn,
    // cancel it so the user's new input feels responsive. The bird
    // stops typing/clicking and returns to follow the cursor.
    this.actionAgent.cancel();
  }

  private registerGlobalHotkeys(): void {
    // Capture phase so we see the keystroke before any element on the
    // page (e.g. a host-site contenteditable that grabs Ctrl+Space)
    // can stopPropagation on it.
    document.addEventListener("keydown", this.handleKeyDown, { capture: true });
    document.addEventListener("keyup", this.handleKeyUp, { capture: true });
    // If the user switches windows / tabs / desktops mid-press, the
    // browser never delivers the keyup. Close the PTT session on any
    // focus-loss signal so the bird can't get stuck listening forever.
    window.addEventListener("blur", this.handleFocusLoss);
    document.addEventListener("visibilitychange", this.handleFocusLoss);
  }

  private removeGlobalHotkeys(): void {
    document.removeEventListener("keydown", this.handleKeyDown, { capture: true });
    document.removeEventListener("keyup", this.handleKeyUp, { capture: true });
    window.removeEventListener("blur", this.handleFocusLoss);
    document.removeEventListener("visibilitychange", this.handleFocusLoss);
  }

  private handleFocusLoss = (): void => {
    if (this.isPushToTalkHeld) {
      void this.endPushToTalk();
    }
  };

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Ctrl+Shift+T toggles text-only mode. Matches native's
    // `GlobalTextModeHotkeyMonitor` behavior (minus system-wide scope).
    if (
      event.key.toLowerCase() === "t" &&
      event.ctrlKey &&
      event.shiftKey &&
      !event.repeat
    ) {
      event.preventDefault();
      this.settings.set("textOnlyMode", !this.settings.get("textOnlyMode"));
      this.pokeActivity();
      return;
    }

    // ESC: hard interrupt — cancel anything in flight (Claude stream,
    // TTS playback, host choreography) and dismiss any open bubble.
    // Always works, regardless of focus, so the user can rescue
    // themselves from a runaway response or a stuck choreography.
    if (event.key === "Escape" && !event.repeat) {
      if (!this.settings.get("sessionActive")) return;
      this.interruptEverything();
      return;
    }

    // `/` opens the typed input bubble. Normally we skip when focus
    // is in a real text field (so the user can type slashes there),
    // but if Koko's choreography owns the focused element OR a
    // response is currently playing, the slash takes precedence —
    // the user clearly wants to break in.
    const isOurChoreographyTyping = this.actionAgent.isBusy();
    const isCurrentlyResponding =
      this.voiceState.get() === "responding" ||
      this.voiceState.get() === "processing";
    const slashShouldOverrideFocus =
      isOurChoreographyTyping || isCurrentlyResponding;
    if (
      event.key === SLASH_HOTKEY &&
      !event.repeat &&
      (slashShouldOverrideFocus || !isTypingTarget(event.target))
    ) {
      if (!this.settings.get("sessionActive")) return;
      event.preventDefault();
      event.stopPropagation();
      // Cancel anything currently happening so the new turn starts
      // from a clean slate.
      this.interruptEverything();
      this.revealBirdIfTransient();
      this.typedInputBubble.show();
      this.pokeActivity();
      return;
    }

    if (matchesPushToTalkBinding(event) && !event.repeat) {
      if (!this.settings.get("sessionActive")) return;
      event.preventDefault();
      event.stopPropagation();
      void this.beginPushToTalk();
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    if (!this.isPushToTalkHeld) return;
    // End PTT when ANY part of the combo is released — the modifier OR
    // the key. We can't require `event.ctrlKey === true` here because
    // by the time keyup fires for "Control", `ctrlKey` is already false
    // (the modifier just got released), and if the user releases Ctrl
    // first the bird would otherwise stay stuck in listening forever.
    const released = event.key;
    if (
      released === " " ||
      released === "`" ||
      released === "Control" ||
      released === "Meta"
    ) {
      event.preventDefault();
      event.stopPropagation();
      void this.endPushToTalk();
    }
  };

  private async beginPushToTalk(): Promise<void> {
    if (this.isPushToTalkHeld) return;
    this.isPushToTalkHeld = true;
    this.revealBirdIfTransient();
    this.pokeActivity();
    this.voiceState.set("listening");
    this.soundEffects.play("listen");
    // Visual confirmation that the hotkey actually fired. macOS binds
    // Ctrl+Space to "Select previous input source" by default — if the
    // OS swallows the event, the user will never see this pill, which
    // is exactly the diagnostic signal we want.
    this.feedbackPill.flash("LISTENING…");

    if (!this.voiceInputController) {
      const module = await import("../input/VoiceInputController");
      this.voiceInputController = new module.VoiceInputController({
        workerBaseUrl: this.config.api,
        onTranscriptionStart: () => {
          // Bird flips from "listening" → "processing" the moment we
          // start uploading audio, so the thinking pose plays during
          // the transcription round trip instead of the listening pose
          // freezing on a stopped mic.
          this.waveform.hide();
          if (this.voiceState.get() === "listening") {
            this.voiceState.set("processing");
            this.soundEffects.play("think");
          }
        },
        onFinalTranscript: (text) => {
          void this.runConversationTurn(text);
        },
        onError: (error) => {
          // Empty-audio + transcription failures both land here. Show a
          // long-form red error pill with the actual upstream message
          // so the user can see WHY voice failed instead of a generic
          // "VOICE ERROR" — the previous mapping silently swallowed
          // the most useful diagnostic (the upstream response body).
          console.warn("[koko] voice error:", error);
          this.feedbackPill.flash(formatVoiceErrorForPill(error), {
            variant: "error",
          });
          this.soundEffects.play("error");
          this.voiceState.set("idle");
          this.isPushToTalkHeld = false;
        },
        onAnalyserReady: (analyserNode) => {
          this.waveform.setAnalyser(analyserNode);
        },
      });
    }

    try {
      await this.voiceInputController.startRecording();
    } catch (error) {
      console.warn("[koko] push-to-talk failed:", error);
      this.feedbackPill.flash(formatVoiceErrorForPill(error as Error));
      this.isPushToTalkHeld = false;
      this.voiceState.set("idle");
    }
  }

  private async endPushToTalk(): Promise<void> {
    if (!this.isPushToTalkHeld) return;
    this.isPushToTalkHeld = false;
    if (!this.voiceInputController) {
      this.voiceState.set("idle");
      return;
    }
    // `stopRecording` resolves only after the upload + transcription
    // round trip completes — by which point either `onFinalTranscript`
    // has kicked off `runConversationTurn` (state → processing/responding)
    // or `onError` has flashed a pill and dropped the state to idle.
    await this.voiceInputController.stopRecording();
  }

  private handleWakeWordDetected(): void {
    if (!this.settings.get("sessionActive")) return;
    // The listener has already disabled itself before invoking this
    // (see `WakeWordListener.handleResult`) so AssemblyAI can take over
    // the mic without contention. We re-enable in `wireVoiceStateToSprite`
    // when voice state returns to "idle" after the turn (incl. TTS).
    this.soundEffects.play("wake");
    this.feedbackPill.flash("HEY KOKO HEARD");
    this.pokeActivity();
    void this.beginPushToTalk();
    // Auto-close the listening window after a fixed silence budget —
    // wake-word turns don't have a key-up event to drive `endPushToTalk`.
    window.setTimeout(() => void this.endPushToTalk(), 3500);
  }
}

/**
 * Translates a voice-input error into a pill message. We map the small
 * set of well-understood failure modes to short labels (MIC BLOCKED,
 * NO MIC FOUND, etc.). Everything else falls through to the *actual*
 * error string from the underlying API — `formatVoiceErrorForPill`
 * used to swallow these as a generic "VOICE ERROR" which made
 * debugging impossible. The error pill variant in `FeedbackPill`
 * wraps multi-line so a long upstream body still fits.
 */
function formatVoiceErrorForPill(error: Error): string {
  const rawMessage = error?.message ?? "unknown voice error";
  const lower = rawMessage.toLowerCase();
  if (
    lower.includes("notallowed") ||
    lower.includes("permission") ||
    lower.includes("denied")
  ) {
    return "mic blocked — allow microphone in browser settings";
  }
  if (lower.includes("notfound") || lower.includes("no mic") || lower.includes("no audio")) {
    return "no microphone found";
  }
  if (lower.includes("transcript was empty") || lower.includes("produced no audio")) {
    return "didn't catch that — try speaking longer";
  }
  // For any upstream / network failure, surface the actual message so
  // the user (and you) can see the real reason — "transcription failed
  // (502): {detail}", "network error: ...", etc. Truncate to keep the
  // pill from filling the whole screen.
  return rawMessage.length > 360 ? `${rawMessage.slice(0, 357)}...` : rawMessage;
}

function isTypingTarget(eventTarget: EventTarget | null): boolean {
  if (!(eventTarget instanceof HTMLElement)) return false;
  const tag = eventTarget.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (eventTarget.isContentEditable) return true;
  return false;
}

function matchesPushToTalkBinding(event: KeyboardEvent): boolean {
  return PUSH_TO_TALK_BINDINGS.some(
    (binding) => event.key === binding.key && event[binding.modifier],
  );
}

/**
 * Sentence-boundary regex for the chunked-TTS pipeline. We treat
 * `.` `!` `?` `…` followed by whitespace (or end-of-string) as a
 * boundary, plus newlines. The negative-lookbehind avoids splitting
 * decimals like "3.5 seconds" or "e.g.".
 */
const SENTENCE_BOUNDARY_REGEX = /(?<![A-Za-z]\.[A-Za-z]|\d)([.!?…]+)(\s+|$)/g;

/**
 * Return the longest prefix of `display.slice(committedChars)` that
 * ends on a complete sentence boundary. Empty string when the next
 * sentence is still being streamed (no boundary yet seen).
 *
 * Used by the chunked-TTS pipeline so each sentence gets enqueued the
 * moment its terminator arrives — first audio plays in ~1 s instead of
 * waiting for Claude to finish the whole response.
 */
function sliceCompletedSentences(display: string, committedChars: number): string {
  if (committedChars >= display.length) return "";
  const tail = display.slice(committedChars);
  let lastBoundaryEnd = -1;
  SENTENCE_BOUNDARY_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SENTENCE_BOUNDARY_REGEX.exec(tail)) !== null) {
    lastBoundaryEnd = match.index + match[0].length;
  }
  if (lastBoundaryEnd === -1) return "";
  return tail.slice(0, lastBoundaryEnd);
}
